import type { FullActivityItem } from '@customTypes/types';
import { ArtistJobs, WorkItemStatus, WorkItemTypes } from '@customTypes/types';
import { Button, Checkbox, Group, Select, Stack, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { errorNotificationStyleConfig } from '@/features/notification/config/errorNotificationConfig';
import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { useAdminUpdateWorkItemMutation } from '@/shared/api/requests/work/admin-update-work-item';
import { LBModalLayout } from '@/shared/layouts';

type EditTaskModalProps = {
  opened: boolean;
  onClose: () => void;
  task: FullActivityItem;
};

export const EditTaskModal = ({ opened, onClose, task }: EditTaskModalProps) => {
  const [status, setStatus] = useState<WorkItemStatus>(task.status);
  const [startDate, setStartDate] = useState<Date | null>(
    task.startDate ? new Date(task.startDate) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    task.endDate ? new Date(task.endDate) : null
  );
  const [ticketCreditation, setTicketCreditation] = useState(task.ticketCreditation);
  const [jobRole, setJobRole] = useState<ArtistJobs>(task.jobRole);
  const [arrivalConfirmed, setArrivalConfirmed] = useState(task.arrivalConfirmed);

  const queryClient = useQueryClient();

  const updateWorkItemMutation = useAdminUpdateWorkItemMutation({
    options: {
      onSuccess: async () => {
        notifications.show({
          ...notificationStyleConfig,
          title: 'Контракт успешно обновлён',
          message: 'Изменения сохранены'
        });
        // Force refetch the kanban board data
        await queryClient.refetchQueries({ queryKey: ['CategorisedActivitiesList'] });
        onClose();
      },
      onError: (error) => {
        notifications.show({
          ...errorNotificationStyleConfig,
          title: 'Ошибка при обновлении контракта',
          message: error.response?.data?.Detail || 'Попробуйте позже'
        });
      }
    }
  });

  const handleSave = () => {
    updateWorkItemMutation.mutate({
      params: {
        ...(task.workItemType === WorkItemTypes.Request
          ? { workRequestId: task.workItemId }
          : { workInviteId: task.workItemId }),
        status,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        ticketCreditation,
        jobRole,
        arrivalConfirmed
      }
    });
  };

  return (
    <LBModalLayout opened={opened} onClose={onClose}>
      <Stack gap='md'>
        <Title order={3}size='h3'>Редактировать контракт</Title>

        <Select
          label='Статус'
          value={status}
          data={[
            { value: WorkItemStatus.Pending, label: 'В ожидании' },
            { value: WorkItemStatus.Accepted, label: 'Принято' },
            { value: WorkItemStatus.Rejected, label: 'Отклонено' },
            { value: WorkItemStatus.Withdrawn, label: 'Отозвано' },
            { value: WorkItemStatus.Expired, label: 'Истекло' },
            { value: WorkItemStatus.ContractOngoing, label: 'Контракт в процессе' },
            {
              value: WorkItemStatus.ContractRequestedEndDate,
              label: 'Запрошена дата окончания'
            },
            { value: WorkItemStatus.ClubSetEndDate, label: 'Клуб установил дату окончания' },
            { value: WorkItemStatus.ContractCompleted, label: 'Контракт завершен' },
            { value: WorkItemStatus.Cancelled, label: 'Отменено' }
          ]}
          onChange={(value) => setStatus(value as WorkItemStatus)}
        />

        <DateInput
          label='Дата начала'
          value={startDate}
          valueFormat='DD.MM.YYYY'
          onChange={setStartDate}
        />

        <DateInput
          label='Дата окончания'
          value={endDate}
          valueFormat='DD.MM.YYYY'
          onChange={setEndDate}
        />

        <Select
          label='Должность'
          value={jobRole}
          data={[
            { value: ArtistJobs.Hostess, label: 'Хостес' },
            { value: ArtistJobs.GoGo, label: 'Гоу-Гоу' },
            { value: ArtistJobs.Strip, label: 'Стрип' }
          ]}
          onChange={(value) => setJobRole(value as ArtistJobs)}
        />

        <Checkbox
          checked={ticketCreditation}
          label='Кредитация билета'
          onChange={(event) => setTicketCreditation(event.currentTarget.checked)}
        />

        <Checkbox
          checked={arrivalConfirmed}
          label='Подтверждение прибытия'
          onChange={(event) => setArrivalConfirmed(event.currentTarget.checked)}
        />

        <Group justify='flex-end' mt='md'>
          <Button disabled={updateWorkItemMutation.isPending} variant='subtle' onClick={onClose}>
            Закрыть
          </Button>
          <Button loading={updateWorkItemMutation.isPending} onClick={handleSave}>
            Сохранить
          </Button>
        </Group>
      </Stack>
    </LBModalLayout>
  );
};
